'use client'
import { Teams } from '@glovo/organisation'
import { ExpeditionType } from '@glovo/perseus-events-core'
import { PageType } from '@glovo/perseus-events-perseus'
import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'next/navigation'
import type { ChangeEvent, FC } from 'react'
import { useCallback, useState } from 'react'
import styles from './UserSearchInput.module.scss'
import { searchBarClickedEvent } from '@/search/analytics'
import { useSearchPageParams } from '@/search/ui/helper'
import { useLocalisation } from '@/tools/i18n/client'
import { useErrorHandling, withErrorBoundary } from '@/tools/o11y/client'
import { useToggles } from '@/tools/toggles/client'
import { Button, ButtonSize, MagnifyingGlassIcon, SearchInput, SearchSize } from '@/tools/ui'

interface Props {
  size?: SearchSize
}

const UserSearchInputComponent: FC<Props> = ({ size = SearchSize.small }: Props) => {
  const { t } = useLocalisation()
  const { query, language, countryCode, citySlug } = useSearchPageParams()
  const { withErrorTracking } = useErrorHandling()
  const [searchInput, setSearchInput] = useState(query ?? '')
  const { toggles } = useToggles()
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const isSearchPageEnabled = toggles.CUSTOMER_WEB_REACT_SEARCH_PAGE_ENABLED

  const redirectToSearchPage = useCallback(
    (searchValue: string) => {
      const sanitisedSearchValue = encodeURIComponent(searchValue)
      const oldPath = `/${countryCode}/${language}/${citySlug}/search-glovo/${sanitisedSearchValue}`
      const newPath = `/${language}/${countryCode}/${citySlug}/search?q=${sanitisedSearchValue}`

      if (isSearchPageEnabled) {
        router.push(newPath)
      } else {
        window.location.assign(oldPath)
      }
    },
    [countryCode, language, citySlug, isSearchPageEnabled, router]
  )

  // Check if we're on a search page where we should update URL instead of navigating
  const isOnSearchPage =
    pathname.endsWith('/search') ||
    pathname.includes('/search?q=') ||
    pathname.includes('/search-glovo')

  const updateSearchQuery = useCallback(
    (searchQuery: string) => {
      const params = new URLSearchParams(searchParams.toString())
      if (searchQuery) {
        params.set('q', searchQuery)
      } else {
        params.delete('q')
      }

      router.replace(`${pathname}?${params.toString()}`)
    },
    [pathname, searchParams, router]
  )

  const handleSearchInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      withErrorTracking(() => {
        setSearchInput(e.target.value)
      })()
    },
    [withErrorTracking]
  )

  const handleSearchClick = useCallback(() => {
    if (isOnSearchPage) {
      // Update URL if we're already on a search page
      updateSearchQuery(searchInput)
    } else {
      // Navigate to search page if we're not on it
      if (searchInput) {
        redirectToSearchPage(searchInput)
      }
    }
  }, [isOnSearchPage, redirectToSearchPage, searchInput, updateSearchQuery])

  const handleSearchFocus = () => {
    const determinePageType = () => {
      if (pathname.includes('/categories')) {
        return PageType.ShopList
      }
      if (pathname.includes('/search')) {
        return PageType.ShopDetails
      }
      return PageType.Home
    }

    searchBarClickedEvent({
      expeditionType: ExpeditionType.Delivery,
      searchTerm: searchInput,
      pageUrlPath: window.location.pathname,
      pageType: determinePageType(),
    })
  }

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        e.preventDefault()
        e.currentTarget.blur()
        void handleSearchClick()
      }
    },
    [handleSearchClick]
  )

  return (
    <div className={styles.inputContainer}>
      <SearchInput
        aria-label={t('store.navigation.search')}
        className={styles.input}
        leadingIcon={MagnifyingGlassIcon}
        placeholder={t('search.general')}
        size={size}
        value={searchInput}
        onChange={handleSearchInput}
        onClick={handleSearchFocus}
        onKeyDown={handleKeyDown}
      />
      <Button size={ButtonSize.small} onClick={handleSearchClick}>
        {t('common.search')}
      </Button>
    </div>
  )
}

export const UserSearchInput = withErrorBoundary(UserSearchInputComponent, {
  id: 'search.user-search-input',
  owner: Teams.WEB,
})
